import { useCallback, useMemo, useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError, isAxiosError } from "axios";

import {
  Configuration,
  DecisionEnvironment,
  EntitiesSourcePreviewRequestFilterCondition,
  SourcesApi,
} from "src/clients/jobs";
import { featureQueryKeys } from "src/featureCatalogue/queries";
import { FormData } from "src/jobs/common/entitySource/types";
import { queryClient } from "src/queryClient";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { logger } from "src/utils/logger";
import { toastError } from "src/utils/toastError";

const queryKeys = {
  mutation: (jobId: string, sourceId: string) => [
    "entity-source-preview-mutation",
    jobId,
    sourceId,
  ],
  preview: (
    jobId: string,
    sourceId: string,
    environment: DecisionEnvironment | null,
    executionToken: string,
  ) => [
    "entity-source-preview",
    jobId,
    sourceId,
    "environment",
    environment ?? "null",
    "execution-token",
    executionToken,
  ],
  previewCount: (executionToken: string | null) => [
    "entity-source-preview-count",
    executionToken ?? "null",
  ],
};

const useSourcesApi = () => {
  const { workspace } = useWorkspaceContext();
  return useMemo(
    () =>
      new SourcesApi(
        new Configuration({ basePath: "https://" + workspace.base_url! }),
      ),
    [workspace.base_url],
  );
};

export type UseEntitySourcePreviewMutationArgs = {
  environment: DecisionEnvironment;
  destinationFieldName: string;
  entitySchema: string;
  filterCondition: FormData["filter_condition"];
};

export type UseEntitySourcePreviewCountMutationArgs = Omit<
  UseEntitySourcePreviewMutationArgs,
  "destinationFieldName"
>;

const useEntitySourcePreviewMutation = (jobId: string, sourceId: string) => {
  const sourcesApi = useSourcesApi();

  return useMutation({
    mutationKey: queryKeys.mutation(jobId, sourceId),
    mutationFn: async ({
      environment,
      destinationFieldName,
      entitySchema,
      filterCondition,
    }: UseEntitySourcePreviewMutationArgs) => {
      const response =
        await sourcesApi.entitiesPreviewSourceJobsApiV2JobsJobIdSourcesIdPreviewPost(
          {
            jobId,
            id: sourceId,
            entitiesSourcePreviewRequest: {
              entity_schema: entitySchema,
              destination_field_name: destinationFieldName,
              environment,
              limit: 100,
              filter_condition:
                filterCondition as unknown as EntitiesSourcePreviewRequestFilterCondition,
            },
          },
        );
      return response.data;
    },
    onError: (error, variables) => {
      if ((error as AxiosError).response?.status === 500) {
        logger.error(
          "Unexpected error: Entities filter preview failed: ",
          error,
          "Query: ",
          variables,
        );
      }
    },
    throwOnError: false,
  });
};

const useGetEntityPreview = (
  jobId: string,
  sourceId: string,
  environment: DecisionEnvironment | null,
  entitySchema: string | null,
  executionToken: string | null,
) => {
  const sourcesApi = useSourcesApi();
  const { workspace } = useWorkspaceContext();

  return useQuery({
    queryKey: queryKeys.preview(
      jobId,
      sourceId,
      environment,
      executionToken ?? "",
    ),
    enabled: !!executionToken && !!entitySchema,
    queryFn: async () => {
      const response =
        await sourcesApi.getPreviewForSourceJobsApiV2JobsJobIdEnvironmentSourcesIdPreviewExecutionIdGet(
          {
            environment: environment as unknown as DecisionEnvironment,
            jobId,
            id: sourceId,
            executionId: executionToken!,
          },
        );

      if (response.status === 200 && response.data.results.length === 0) {
        throw new Error(
          "No data found for the selected filters. Please adjust the filters, and try running the query again.",
        );
      }

      if (response.status === 200 && response.data.results.length > 0) {
        // clear feature cache
        await queryClient.invalidateQueries({
          queryKey: featureQueryKeys.entityTypeFeatures(
            workspace.id,
            environment === DecisionEnvironment.SANDBOX ? "sandbox" : "live",
            entitySchema!,
          ),
        });
      }

      return response;
    },
    refetchInterval: (data) => {
      return data?.state.data?.status === 202 && !data?.state.error
        ? 2000
        : false;
    },
  });
};

export const useEntitySourcePreview = (jobId: string, sourceId: string) => {
  const [executionToken, setExecutionToken] = useState<string | null>(null);
  const [entitySchema, setEntitySchema] = useState<string | null>(null);
  const {
    mutateAsync,
    error: mutationError,
    isPending: isMutating,
    variables,
  } = useEntitySourcePreviewMutation(jobId, sourceId);

  const {
    data: previewData,
    isFetching,
    error,
  } = useGetEntityPreview(
    jobId,
    sourceId,
    variables?.environment ?? null,
    entitySchema,
    executionToken,
  );

  const triggerPreview = useCallback(
    async (args: UseEntitySourcePreviewMutationArgs) => {
      setExecutionToken(null);

      try {
        const { execution_id } = await mutateAsync(args);
        setExecutionToken(execution_id);
        setEntitySchema(args.entitySchema);
      } catch (error) {
        let errObj = error;

        if (isAxiosError(error)) {
          errObj = error.response?.data;
        }

        toastError("Failed to preview Entities");

        throw errObj;
      }
    },
    [mutateAsync],
  );

  const isPending =
    isMutating ||
    (isFetching && !error) ||
    (!error && previewData?.status === 202);

  const errorObj = mutationError ?? error;

  return {
    triggerPreview,
    previewData: isPending ? undefined : previewData?.data,
    environment: variables?.environment ?? null,
    isPending,
    error: isAxiosError(errorObj) ? errorObj.response?.data : errorObj,
  };
};

const useGetEntitySourcePreviewCount = (executionToken: string | null) => {
  const sourcesApi = useSourcesApi();

  return useQuery({
    queryKey: queryKeys.previewCount(executionToken),
    queryFn: async () => {
      const response =
        await sourcesApi.getEntityCountJobsApiV2EntitiesCountExecutionIdGet({
          executionId: executionToken!,
        });

      return {
        status: response.status,
        data: response.data,
      };
    },
    enabled: !!executionToken,
    refetchInterval: (data) => {
      return data.state.data?.status === 202 && !data.state.error
        ? 2000
        : false;
    },
  });
};

const useStartEntitySourcePreviewCount = () => {
  const sourcesApi = useSourcesApi();

  return useMutation({
    mutationFn: async ({
      environment,
      entitySchema,
      filterCondition,
    }: UseEntitySourcePreviewCountMutationArgs) => {
      const response =
        await sourcesApi.startEntityCountJobsApiV2EntitiesCountPost({
          startEntityCountRequest: {
            environment,
            entity_schema: entitySchema,
            filter_condition:
              filterCondition as unknown as EntitiesSourcePreviewRequestFilterCondition,
          },
        });

      return response.data;
    },
  });
};

export const useEntitySourcePreviewCount = () => {
  const [executionToken, setExecutionToken] = useState<string | null>(null);
  const {
    mutateAsync,
    error: mutationError,
    isPending: isMutating,
  } = useStartEntitySourcePreviewCount();

  const {
    data: countData,
    isFetching,
    error,
  } = useGetEntitySourcePreviewCount(executionToken);

  const triggerCount = useCallback(
    async (args: UseEntitySourcePreviewCountMutationArgs) => {
      setExecutionToken(null);

      try {
        const { execution_id } = await mutateAsync(args);
        setExecutionToken(execution_id);
      } catch (error) {
        logger.error("Failed to count preview Entities", error);
      }
    },
    [mutateAsync],
  );

  const isPending =
    isMutating ||
    (isFetching && !error) ||
    (!error && countData?.status === 202);

  return {
    triggerCount,
    countData: isPending ? undefined : countData?.data,
    isPending,
    error: mutationError ?? error,
  };
};
